import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FaInfoCircle } from 'react-icons/fa';
import download from 'downloadjs';
import React, { useContext, useEffect, useState } from 'react';
import { FiDownload, FiSearch } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import {
  Button,
  Datepicker,
  Heading,
  Input,
  Menu,
  Pagination,
  Select,
  StatusBadge,
  Table,
} from '~/components';
import { useDebounce, useInvoices, useQueryParams } from '~/hooks';
import { getBillPdf, getInvoicePdf, getInvoicesXlsx } from '~/services';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { PAGE_SIZE } from '~/constants';
import { updateUrlParams } from '~/utils/updateUrlParams';
import { isValidDate } from '~/utils/isValidDate';
import { getLastDayOfMonthAfterXMonths } from '~/utils/getLastDayOfMonthAfterXMonths';

export const Invoices: React.VFC = () => {
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();

  const toast = useToast();

  const parseDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      situacao: queryParams.get('situacao') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
      order: queryParams.get('order') ?? '',
      orderBy: queryParams.get('orderBy') ?? '',
      periodo_inicial: queryParams.get('periodo_inicial') ?? '',
      periodo_final: queryParams.get('periodo_final') ?? '',
    };
  });

  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isDownloadingBill, setIsDownloadingBill] = useState(false);
  const { groups, headOffices, branches } = useContext(FilterContext);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const [maxDataFinal, setMaxDataFinal] = useState<Date | undefined>(() => {
    return undefined;
  });

  const [dataInicial, setDataInicial] = useState<Date | undefined>(() => {
    if (
      parametros.periodo_inicial &&
      !isValidDate(parametros.periodo_inicial)
    ) {
      return undefined;
    }
    return parametros.periodo_inicial
      ? parseDate(parametros.periodo_inicial)
      : undefined;
  });

  const validOrders = ['vr_fatura', 'dt_emissaofatura', 'dt_vencimentofatura'];
  const validOrderBy = ['DESC', 'ASC'];
  const validPageSizes = ['10', '25', '50', '100'];

  const { data, isLoading, isFetching, error, refetch } = useInvoices(
    Number(parametros.pagina) ?? 1,
    parametros.busca,
    parametros.situacao,
    parametros.periodo_inicial,
    parametros.periodo_final,
    parametros.orderBy,
    parametros.order,
    { groups, headOffices, branches },
    Number(parametros.paginacao) ?? PAGE_SIZE,
    {},
  );

  useDebounce(
    () => {
      setParametros({
        ...parametros,
        busca: searchTmp,
        pagina: '1',
      });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
      refetch();
    },
    1000,
    [searchTmp],
  );

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    setParametros({
      ...parametros,
      pagina: '1',
    });
    updateUrlParams({ ...parametros, pagina: '1' }, history);
    refetch();
  }, [groups, headOffices, branches]);

  useEffect(() => {
    const pagina = queryParams.get('pagina') ?? '1';
    const paginaValida = Number(pagina) < 1 ? '1' : pagina;

    const pageSize = queryParams.get('paginacao') ?? '10';
    const validPageSize = validPageSizes.includes(pageSize) ? pageSize : '10';

    let periodo_inicial = queryParams.get('periodo_inicial') ?? '';
    let periodo_final = queryParams.get('periodo_final') ?? '';

    if (
      periodo_inicial &&
      periodo_final &&
      (!isValidDate(periodo_inicial) || !isValidDate(periodo_final))
    ) {
      periodo_inicial = '';
      periodo_final = '';
    }

    setParametros({
      ...parametros,
      pagina: paginaValida,
      order: queryParams.get('order') ?? sortDirection,
      orderBy: queryParams.get('orderBy') ?? sortField,
      paginacao: validPageSize,
      periodo_inicial,
      periodo_final,
    });
    updateUrlParams(
      {
        ...parametros,
        pagina: paginaValida,
        order: queryParams.get('order') ?? sortDirection,
        orderBy: queryParams.get('orderBy') ?? sortField,
        paginacao: validPageSize,
        periodo_inicial,
        periodo_final,
      },
      history,
    );
  }, [location.search]);

  const [sortField, setSortField] = useState(() => {
    const orderBy = queryParams.get('orderBy') ?? 'dt_emissaofatura';
    return validOrders.includes(orderBy) ? orderBy : 'dt_emissaofatura';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const order = queryParams.get('order') ?? 'DESC';
    return validOrderBy.includes(order) ? order : 'DESC';
  });

  function handleInvoiceOrder(field: string) {
    setSortField(field);
    setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
  }

  useEffect(() => {
    const pagina = queryParams.get('pagina') ?? '1';
    const paginaValida = Number(pagina) < 1 ? '1' : pagina;

    const pageSize = queryParams.get('paginacao') ?? '10';
    const validPageSize = validPageSizes.includes(pageSize) ? pageSize : '10';

    let periodo_inicial = queryParams.get('periodo_inicial') ?? '';
    let periodo_final = queryParams.get('periodo_final') ?? '';

    if (
      periodo_inicial &&
      periodo_final &&
      (!isValidDate(periodo_inicial) || !isValidDate(periodo_final))
    ) {
      periodo_inicial = '';
      periodo_final = '';
    }

    setParametros({
      ...parametros,
      order: sortDirection,
      orderBy: sortField,
      pagina: paginaValida,
      paginacao: validPageSize,
      periodo_inicial,
      periodo_final,
    });
    updateUrlParams(
      {
        ...parametros,
        order: sortDirection,
        orderBy: sortField,
        pagina: paginaValida,
        paginacao: validPageSize,
        periodo_inicial,
        periodo_final,
      },
      history,
    );
  }, [sortField, sortDirection]);

  const getDirection = (key: string) => {
    return sortField === key ? sortDirection : undefined;
  };

  function handleInvoiceDownload(cd_fatura: string, nu_fatura: string) {
    setIsDownloadingInvoice(true);
    getInvoicePdf(cd_fatura)
      .then(r => {
        setIsDownloadingInvoice(false);
        download(
          atob(r?.data),
          `fatura_${cd_fatura}_${nu_fatura}`,
          'application/pdf',
        );
      })
      .catch((e: any) => {
        setIsDownloadingInvoice(false);
        if (e?.response?.status === 404) {
          toast({
            title: e.response.data.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Falha ao baixar fatura',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
  }

  function handleDownloadBill(cd_fatura: string, nu_fatura: string) {
    setIsDownloadingBill(true);
    getBillPdf(cd_fatura)
      .then(response => {
        setIsDownloadingBill(false);
        download(
          atob(response?.data),
          `boleto_${cd_fatura}_${nu_fatura}`,
          'application/pdf',
        );
      })
      .catch((e: any) => {
        setIsDownloadingBill(false);
        if (e?.response?.status === 404) {
          toast({
            title: e.response.data.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Falha ao baixar boleto',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
  }

  function handleInvoiceStatusChange(e: any) {
    setParametros({
      ...parametros,
      situacao: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        pagina: '1',
        situacao: e.target.value,
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  }

  const handlePageSizeChange = (e: any) => {
    setParametros({
      ...parametros,
      paginacao: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        pagina: '1',
        paginacao: e.target.value,
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getInvoicesXlsx(
      parametros.busca,
      parametros.situacao,
      parametros.periodo_inicial,
      parametros.periodo_final,
      parametros.orderBy,
      parametros.order,
      { groups, headOffices, branches },
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const handlePeriodoInicial = (date: Date | null) => {
    let data_final = date;
    if (data_final) {
      data_final = getLastDayOfMonthAfterXMonths(data_final, 6);
    }

    setMaxDataFinal(data_final ?? undefined);
    setDataInicial(date ?? undefined);

    setParametros({
      ...parametros,
      periodo_inicial: date ? format(date, 'yyyy-MM-dd') : '',
      periodo_final: data_final ? format(data_final, 'yyyy-MM-dd') : '',
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        periodo_inicial: date ? format(date, 'yyyy-MM-dd') : '',
        periodo_final: data_final ? format(data_final, 'yyyy-MM-dd') : '',
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const handlePeriodoFinal = (date: Date | null) => {
    setParametros({
      ...parametros,
      periodo_final: date ? format(date, 'yyyy-MM-dd') : '',
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        periodo_final: date ? format(date, 'yyyy-MM-dd') : '',
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const valoresFatura = (valores_contratos: string[][]) => {
    return (
      <HStack gridGap="4">
        <Stack>
          <Text>Faturas consolidadas:</Text>
          <ul style={{ paddingLeft: '15px' }}>
            {valores_contratos.map(valor => (
              <li key={valor[0]}>{valor[0]}</li>
            ))}
          </ul>
        </Stack>
        <Stack>
          <Text fontWeight="bold">Contratos:</Text>
          <ul>
            {valores_contratos.map(valor => (
              <Text key={valor[1]} fontWeight="bold">
                {valor[1]}
              </Text>
            ))}
          </ul>
        </Stack>
      </HStack>
    );
  };

  return (
    <Box px="8" py="4">
      <Heading>Faturas</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={3}>
          <Flex align="end" gridGap={4} mb={4}>
            <Flex align="end" gridGap={4}>
              <Input
                name="search"
                type="text"
                icon={FiSearch}
                value={searchTmp}
                onChange={e => setSearchTmp(e.target.value)}
                placeholder="Procure por fatura, CNPJ ou Grupo Econômico"
                w={370}
                fontSize="15px"
              />
              <Select
                name="tp_situacao"
                label="Situação"
                onChange={e => handleInvoiceStatusChange(e)}
                value={parametros.situacao}
              >
                <option value="">Todas</option>
                <option value="1">Pago</option>
                <option value="0">Em aberto</option>
                <option value="2">Vencidas</option>
              </Select>
              <Datepicker
                label="Período"
                onChange={e => handlePeriodoInicial(e)}
                selectedDate={dataInicial || undefined}
                isClearable
                minDate={new Date(2022, 0, 1)}
              />
              <Text>a</Text>
              <Datepicker
                onChange={e => handlePeriodoFinal(e)}
                selectedDate={
                  parametros.periodo_final
                    ? parseDate(parametros.periodo_final)
                    : undefined
                }
                isClearable
                minDate={
                  parametros.periodo_inicial
                    ? parseDate(parametros.periodo_inicial)
                    : undefined
                }
                maxDate={maxDataFinal}
                isDisabled={!parametros.periodo_inicial}
              />
            </Flex>
            {(isFetching || isDownloadingInvoice || isDownloadingBill) && (
              <Spinner mb={2} />
            )}
          </Flex>
          <Button
            disabled={isLoadingDownload || isLoading}
            onClick={handleDownload}
            color="white"
            background="gray.900"
            _hover={{
              bgColor: 'black',
            }}
            style={{ marginTop: '20px' }}
          >
            Exportar
            {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
          </Button>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados das faturas.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th w="5%">N° Fatura</Th>
                  <Th w="10%">CNPJ</Th>
                  <Th w="30%">Empresa/Grupo</Th>
                  <Th w="5%">&ensp;</Th>
                  <Th w="10%">
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() => handleInvoiceOrder('vr_fatura')}
                    >
                      Valor Total{' '}
                      {getDirection('vr_fatura') === 'ASC' ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      )}
                    </Flex>
                  </Th>
                  <Th w="10%">
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() => handleInvoiceOrder('dt_emissaofatura')}
                    >
                      Emissão{' '}
                      {getDirection('dt_emissaofatura') === 'ASC' ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      )}
                    </Flex>
                  </Th>
                  <Th w="10%">
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() => handleInvoiceOrder('dt_vencimentofatura')}
                    >
                      Vencimento{' '}
                      {getDirection('dt_vencimentofatura') === 'ASC' ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      )}
                    </Flex>
                  </Th>
                  <Th w="10%">Situação</Th>
                  <Th w="5%">Fatura</Th>
                  <Th w="5%">2° via Boleto</Th>
                  <Th w="5%" style={{ display: 'none' }} />
                </Tr>
              </Thead>
              <Tbody>
                {data &&
                  data.results.map(invoice => (
                    <Tr key={`${invoice.id_fatura}`}>
                      <Td>
                        <Link to={`/portal/invoices/${invoice.id_fatura}`}>
                          <Text
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                          >
                            {invoice.nu_fatura}
                          </Text>
                        </Link>
                      </Td>
                      <Td>{invoice.empresas[0]?.cnpjFormatado || '-'}</Td>
                      <Td
                        fontSize="15px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        maxWidth="200px"
                        p="0.5rem !important"
                        style={{ borderColor: '#e5e5e6' }}
                      >
                        <Tooltip
                          label={invoice.empresas[0]?.nm_fantasia}
                          placement="top-start"
                        >
                          {invoice.empresas[0]?.nm_fantasia ?? '-'}
                        </Tooltip>
                        <Text
                          fontSize="11px"
                          fontWeight="500"
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          <Tooltip
                            label={
                              invoice.empresas[0]?.grupoeconomico
                                .nm_grupoeconomico
                            }
                            placement="top-start"
                          >
                            {invoice.empresas[0]?.grupoeconomico
                              .nm_grupoeconomico ?? '-'}
                          </Tooltip>
                        </Text>
                      </Td>
                      <Td>
                        {invoice.fatura_consolidada && (
                          <Popover placement="bottom">
                            <PopoverTrigger>
                              <button type="button">
                                <Icon as={FaInfoCircle} />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              color="white"
                              bg="gray.900"
                              borderColor="gray.900"
                            >
                              <PopoverArrow bg="gray.900" />
                              <PopoverBody>
                                {valoresFatura(
                                  invoice.formattedValoresNumeroContratos,
                                )}
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        )}
                      </Td>
                      <Td>{invoice.formattedValue || '-'}</Td>
                      <Td>{invoice.formattedIssueDate || '-'}</Td>
                      <Td>{invoice.formattedDueDate || '-'}</Td>
                      <Td>
                        <StatusBadge variant={invoice.colorScheme}>
                          {invoice.formattedStatus}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <Tooltip label="Baixar Fatura">
                          <IconButton
                            colorScheme="gray"
                            background="transparent"
                            _hover={{
                              background: 'transparent',
                            }}
                            aria-label="Send email"
                            icon={<Icon as={FiDownload} />}
                            onClick={() =>
                              handleInvoiceDownload(
                                invoice.cd_fatura,
                                invoice.nu_fatura,
                              )
                            }
                          />
                        </Tooltip>
                      </Td>
                      <Td>
                        <Tooltip label="Baixar Boleto">
                          <IconButton
                            disabled={invoice?.status === 1}
                            colorScheme="gray"
                            background="transparent"
                            _hover={{
                              background: 'transparent',
                            }}
                            aria-label="Send email"
                            icon={<Icon as={FiDownload} />}
                            onClick={() =>
                              handleDownloadBill(
                                invoice.cd_fatura,
                                invoice.nu_fatura,
                              )
                            }
                          />
                        </Tooltip>
                      </Td>
                      <Td style={{ display: 'none' }}>
                        <Menu>
                          <MenuList>
                            <Link to={`/portal/invoices/${invoice.id_fatura}`}>
                              <MenuItem>Visualizar detalhes</MenuItem>
                            </Link>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              {data && (
                <Pagination
                  totalCountOfRegisters={data.count}
                  currentPage={
                    Number(parametros.pagina) && Number(parametros.pagina) > 1
                      ? Number(parametros.pagina)
                      : 1
                  }
                  onPageChange={_page => {
                    setParametros({
                      ...parametros,
                      pagina: _page.toString(),
                    });
                    updateUrlParams(
                      { ...parametros, pagina: _page.toString() },
                      history,
                    );
                  }}
                  onPageSizeChange={handlePageSizeChange}
                  registersPerPage={Number(parametros.paginacao) ?? PAGE_SIZE}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
